/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/GetBaseDir.ts
 * @create:      2025-07-01 17:59:27.525
 * @modify:      2025-07-11 21:19:12.921
 * @version:     6.0.0
 * @times:       8
 * @lines:       67
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { Strings_Empty, Strings_Slash, Strings_BackSlash } from "../const/Strings";
import { TrimPath } from "./TrimPath";

/**
 * This function extracts the base directory from the provided `pathStr`.
 * If `trimLastSlash` is true, the trailing slash or backslash is removed from the result.
 * Handles both forward slashes (`/`) and backslashes (`\`) as path separators.
 *
 * @param pathStr - The input path string from which to extract the base directory.
 * @param trimLastSlash - Optional. If true, trims the trailing slash or backslash from the result. Defaults to false.
 * @returns The base directory of the given path string, optionally trimmed of its trailing slash or backslash.
 * 
 * @example C:/a/b/c  return C:/a/b/
 * 
 * @since 1.0.5
 * @version 2025-07-03
 */
export function GetBaseDir(pathStr: string, trimLastSlash = false): string {
    if (!pathStr) {
        return Strings_Empty;
    }

    let result = pathStr;

    const trimmed = TrimPath(pathStr);
    const index = Math.max(trimmed.lastIndexOf(Strings_Slash), trimmed.lastIndexOf(Strings_BackSlash));
    if (index === 0) {
        result = trimmed.substring(0, 1);
    } else if (index > 0) {
        result = trimmed.substring(0, trimLastSlash ? index : index + 1);
    }

    return trimLastSlash ? TrimPath(result) : result;
}
